import { useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Select,
  SimpleTable,
  StatusBadge,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui'
import {
  useContractCustomFields,
  useCreateContractCustomField,
  useDeleteContractCustomField,
  useUpdateContractCustomField,
} from '../api/customFieldsApi.ts'
import type {
  ContractCustomField,
  CreateCustomFieldInput,
  CustomFieldOption,
  CustomFieldSection,
  CustomFieldType,
} from '../types.ts'

const TYPE_OPTIONS: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Text (einzeilig)' },
  { value: 'textarea', label: 'Text (mehrzeilig)' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'select', label: 'Auswahlliste' },
  { value: 'boolean', label: 'Ja / Nein' },
]

const SECTION_OPTIONS: { value: CustomFieldSection; label: string }[] = [
  { value: 'identification', label: 'Identifikation & Status' },
  { value: 'party', label: 'Vertragspartei' },
  { value: 'service', label: 'Leistung' },
  { value: 'term', label: 'Laufzeit & Fristen' },
  { value: 'billing', label: 'Abrechnung' },
  { value: 'termination', label: 'Kündigung' },
  { value: 'additional', label: 'Zusatzfelder' },
]

const SECTION_LABEL: Record<CustomFieldSection, string> = Object.fromEntries(
  SECTION_OPTIONS.map((s) => [s.value, s.label]),
) as Record<CustomFieldSection, string>

const TYPE_LABEL: Record<CustomFieldType, string> = Object.fromEntries(
  TYPE_OPTIONS.map((t) => [t.value, t.label]),
) as Record<CustomFieldType, string>

export function CustomFieldsSection() {
  const { toast } = useToast()
  const { data: fields = [], isLoading, error, refetch } = useContractCustomFields()
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<ContractCustomField | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<ContractCustomField | null>(null)
  const del = useDeleteContractCustomField()

  const groups = useMemo(() => {
    const map = new Map<CustomFieldSection, ContractCustomField[]>()
    for (const f of fields) {
      const arr = map.get(f.section) ?? []
      arr.push(f)
      map.set(f.section, arr)
    }
    return SECTION_OPTIONS.filter((s) => map.has(s.value)).map((s) => ({
      section: s.value,
      label: s.label,
      fields: (map.get(s.value) ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    }))
  }, [fields])

  if (isLoading) return <LoadingState label="Lade Zusatzfelder …" />
  if (error) {
    return (
      <ErrorState
        description="Zusatzfelder konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-small text-gray-600 max-w-3xl">
          Eigene Felder für Verträge — vergleichbar mit den UDFs in SAP. Werden im Stammdaten-Tab
          unterhalb der Standard-Felder (Sektion „Zusatzfelder") oder direkt in der gewählten
          Sektion eingeblendet.
        </p>
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<Plus className="w-4 h-4" />}
          onClick={() => {
            setEditing(null)
            setEditorOpen(true)
          }}
        >
          Neues Feld
        </Button>
      </div>

      {groups.length === 0 ? (
        <Card>
          <Card.Body>
            <EmptyState
              title="Noch keine Zusatzfelder"
              description="Lege dein erstes Zusatzfeld an, um z. B. Kostenstelle, Projektnummer oder Lieferanten-Referenz pro Vertrag zu erfassen."
            />
          </Card.Body>
        </Card>
      ) : (
        groups.map((g) => (
          <Card key={g.section}>
            <Card.Header>
              <Card.Title>{g.label}</Card.Title>
              <span className="text-caption text-gray-500">{g.fields.length} Feld(er)</span>
            </Card.Header>
            <Card.Body className="p-0">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.Tr>
                    <SimpleTable.Th>Label</SimpleTable.Th>
                    <SimpleTable.Th>Schlüssel</SimpleTable.Th>
                    <SimpleTable.Th>Typ</SimpleTable.Th>
                    <SimpleTable.Th className="w-24">Pflicht</SimpleTable.Th>
                    <SimpleTable.Th className="w-28 text-right">Reihenfolge</SimpleTable.Th>
                    <SimpleTable.Th className="w-24">Status</SimpleTable.Th>
                    <SimpleTable.Th className="w-24" />
                  </SimpleTable.Tr>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {g.fields.map((f) => (
                    <SimpleTable.Tr key={f.id}>
                      <SimpleTable.Td>{f.label}</SimpleTable.Td>
                      <SimpleTable.Td>
                        <code className="text-caption text-gray-600">{f.fieldKey}</code>
                      </SimpleTable.Td>
                      <SimpleTable.Td>
                        <span className="text-caption text-gray-700">{TYPE_LABEL[f.fieldType]}</span>
                      </SimpleTable.Td>
                      <SimpleTable.Td>
                        {f.required ? (
                          <StatusBadge status="active" label="Pflicht" />
                        ) : (
                          <span className="text-caption text-gray-400">—</span>
                        )}
                      </SimpleTable.Td>
                      <SimpleTable.Td className="text-right tabular-nums">
                        {f.sortOrder}
                      </SimpleTable.Td>
                      <SimpleTable.Td>
                        <StatusBadge status={f.active ? 'active' : 'inactive'} />
                      </SimpleTable.Td>
                      <SimpleTable.Td>
                        <div className="flex justify-end gap-1">
                          <IconButton
                            icon={<Pencil className="w-4 h-4" />}
                            aria-label="Bearbeiten"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(f)
                              setEditorOpen(true)
                            }}
                          />
                          <IconButton
                            icon={<Trash2 className="w-4 h-4" />}
                            aria-label="Löschen"
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDelete(f)}
                          />
                        </div>
                      </SimpleTable.Td>
                    </SimpleTable.Tr>
                  ))}
                </SimpleTable.Body>
              </SimpleTable>
            </Card.Body>
          </Card>
        ))
      )}

      {editorOpen && (
        <CustomFieldEditor
          field={editing}
          onClose={() => {
            setEditorOpen(false)
            setEditing(null)
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Zusatzfeld löschen?"
        message={
          confirmDelete
            ? `„${confirmDelete.label}" und alle bereits gespeicherten Werte in Verträgen werden gelöscht.`
            : ''
        }
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Zusatzfeld gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({
                title: 'Löschen fehlgeschlagen',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              })
              setConfirmDelete(null)
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}

interface CustomFieldEditorProps {
  field: ContractCustomField | null
  onClose: () => void
}

function CustomFieldEditor({ field, onClose }: CustomFieldEditorProps) {
  const { toast } = useToast()
  const create = useCreateContractCustomField()
  const update = useUpdateContractCustomField()
  const isEdit = Boolean(field)

  const [label, setLabel] = useState(field?.label ?? '')
  const [fieldKey, setFieldKey] = useState(field?.fieldKey ?? '')
  const [fieldType, setFieldType] = useState<CustomFieldType>(field?.fieldType ?? 'text')
  const [section, setSection] = useState<CustomFieldSection>(field?.section ?? 'additional')
  const [hint, setHint] = useState(field?.hint ?? '')
  const [required, setRequired] = useState(field?.required ?? false)
  const [active, setActive] = useState(field?.active ?? true)
  const [sortOrder, setSortOrder] = useState(String(field?.sortOrder ?? 0))
  const [optionsText, setOptionsText] = useState(
    field?.optionsJson ? field.optionsJson.map((o) => `${o.value}|${o.label}`).join('\n') : '',
  )

  function parseOptions(): CustomFieldOption[] | null {
    if (fieldType !== 'select') return null
    const lines = optionsText.split('\n').map((l) => l.trim()).filter(Boolean)
    return lines.map((line) => {
      const [value, ...rest] = line.split('|')
      const v = (value ?? '').trim()
      const lbl = rest.join('|').trim() || v
      return { value: v, label: lbl }
    })
  }

  function handleSave() {
    if (!label.trim()) {
      toast({ title: 'Label ist Pflicht', tone: 'danger' })
      return
    }
    const input: CreateCustomFieldInput = {
      label: label.trim(),
      fieldKey: fieldKey.trim() || undefined,
      fieldType,
      section,
      hint: hint.trim() || null,
      required,
      active,
      sortOrder: Number(sortOrder) || 0,
      optionsJson: parseOptions(),
    }

    const onSuccess = () => {
      toast({ title: isEdit ? 'Zusatzfeld aktualisiert' : 'Zusatzfeld angelegt', tone: 'success' })
      onClose()
    }
    const onError = (err: Error) => {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: err.message,
        tone: 'danger',
      })
    }

    if (isEdit && field) {
      update.mutate({ id: field.id, patch: input }, { onSuccess, onError })
    } else {
      create.mutate(input, { onSuccess, onError })
    }
  }

  const saving = create.isPending || update.isPending

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={isEdit ? 'Zusatzfeld bearbeiten' : 'Neues Zusatzfeld'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
            Speichern
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Label"
            hint="Wird im Vertrag als Feld-Beschriftung angezeigt."
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            required
          />
          <Input
            label="Schlüssel (optional)"
            hint="Technischer Identifier — wird automatisch aus dem Label gebildet."
            value={fieldKey}
            onChange={(e) => setFieldKey(e.currentTarget.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select<CustomFieldType>
            label="Typ"
            value={fieldType}
            onValueChange={setFieldType}
            options={TYPE_OPTIONS}
          />
          <Select<CustomFieldSection>
            label="Sektion"
            hint="Bestimmt, in welcher Karte das Feld im Stammdaten-Tab erscheint."
            value={section}
            onValueChange={setSection}
            options={SECTION_OPTIONS}
          />
        </div>

        <Input
          label="Hilfetext (optional)"
          value={hint}
          onChange={(e) => setHint(e.currentTarget.value)}
        />

        {fieldType === 'select' && (
          <Textarea
            label="Optionen (eine pro Zeile, Format: wert|Label)"
            hint="Beispiel: aktiv|Aktiver Vertrag — Wenn nur ein Wert angegeben wird, wird er auch als Label genutzt."
            rows={5}
            value={optionsText}
            onChange={(e) => setOptionsText(e.currentTarget.value)}
          />
        )}

        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Reihenfolge"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.currentTarget.value)}
          />
          <div className="flex items-end pb-2">
            <Switch
              label="Pflichtfeld"
              checked={required}
              onChange={(e) => setRequired(e.currentTarget.checked)}
            />
          </div>
          <div className="flex items-end pb-2">
            <Switch
              label="Aktiv"
              checked={active}
              onChange={(e) => setActive(e.currentTarget.checked)}
            />
          </div>
        </div>

        <p className="text-caption text-gray-500">
          Sektion „{SECTION_LABEL[section]}". Inaktive Felder werden im Vertrag ausgeblendet, ihre
          Werte aber nicht gelöscht.
        </p>
      </div>
    </Modal>
  )
}
